<template>
  <UikitDialog
    :model-value="modelValue"
    position="right"
    full-height
    content-class="osk-size-count-dialog"
    @update:model-value="emits('update:modelValue', $event)"
  >
    <UikitForm
      class="osk-size-count-dialog__form"
      @submit-validate-success="handleChangeSizeOrCount"
    >
      <div class="osk-size-count-dialog__form-sizes-select">
        <div
          v-if="isSizes"
          class="osk-size-count-dialog__form-sizes"
          @click="openSizeDialog"
        >
          {{ $t('components.product.productSizeCountDialog.sizesSelect') }}
        </div>

        <UikitFormSelect
          v-model="currentSizeId"
          name="sizeId"
          :label="$t('components.product.productSizeCountDialog.currentSize')"
          :placeholder="String(product?.size?.value)"
          :disabled="(sizesSelectValues.length <= 1)"
        >
          <UikitFormSelectOption
            v-for="item in sizesSelectValues"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </UikitFormSelect>
      </div>

      <ProductAmountPicker
        name="count"
        :label="$t('components.product.productSizeCountDialog.count')"
        :product="product"
      />

      <UikitButton
        class="osk-size-count-dialog__form-button"
        :loading="loadingDialog"
        full-width
      >
        {{ $t('common.ready') }}
      </UikitButton>
    </UikitForm>
  </UikitDialog>
</template>

<script setup lang="ts">
import { useCart } from '~/services/cart';

import {
  SizeValueDTORes, OrderPositionDTORes, Size,
} from '~/restAPI/data-contracts';
import { isCustomHTTPError } from '@/restAPI/error';

const props = defineProps<{
  modelValue: boolean,
  product?: OrderPositionDTORes
  size?: SizeValueDTORes
}>();

const emits = defineEmits<{
  (e: 'update:modelValue', value: boolean): void,
  (e: 'openSizeDialog', size?: SizeValueDTORes): void,
}>();

const { changeSizeOrCount } = useCart();
const { updateCart } = useCartStore();

const loadingDialog = ref(false);
const currentSizeId = ref(props.product?.size?.id);

const canShowSelectSize = computed(() => props.product?.sizes && props.product.sizes.length > 1);

const currentSize = computed(() => props.product?.sizes?.find((size) => size.id === currentSizeId.value));

const sizesSelectValues = computed(() => {
  if (!props.product) return [];

  const sizes = props.product?.sizes;
  const size = props.product?.size;

  const sizesResult: (SizeValueDTORes | Size)[] = sizes ?? (size ? [size] : []);

  return sizesResult.map((s) => ({
    id: s.id,
    name: !canShowSelectSize.value ? size?.value : getOptionLabelSize(s),
  }));
});

const isSizes = computed(() => props.product?.sizes?.length && props.product.sizes[0].categorySizeType !== 'NO_SIZE');

async function handleChangeSizeOrCount({ values }: { values: { count?: number, sizeId?: number }}) {
  loadingDialog.value = true;

  const { error } = await useAsyncData(
    'changeSizeOrCountPageCart',
    () => changeSizeOrCount({
      orderPositionId: props.product?.id,
      sizeId: values.sizeId,
      count: values.count,
    }).then((res) => {
      updateCart(res);

      return true;
    }),
  );

  if (!error.value) {
    emits('update:modelValue', false);
  } else if (isCustomHTTPError(error.value) && error.value.response.data?.humanMessage) {
    alert(error.value.response.data.humanMessage);
  }

  loadingDialog.value = false;
}

function getOptionLabelSize(s: SizeValueDTORes) {
  return `${s.productSizeType !== 'NO_SIZE' ? s.interestingSizeType : ''} ${s.interestingSizeValue}`;
}

function openSizeDialog() {
  emits('openSizeDialog', currentSize.value);
}

watch(() => props.size, (value) => {
  currentSizeId.value = value?.id;
});

watch(() => props.product, (product) => {
  if (product) {
    currentSizeId.value = product?.size?.id;
  }
});
</script>

<style lang="scss">
@import "~/assets/scss/settings/index";

.osk-size-count-dialog {
  &__form {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 52px;

    @include media-query(md-and-up) {
      width: 504px;
    }

    .osk-field__label {
      @include font-style($font-size-heading, $line-height-heading, $font-weight-bold);
      color: $black;
    }
  }

  &__form-sizes-select {
    position: relative;
    margin-bottom: 42px;
  }

  &__form-sizes {
    font-weight: $font-weight-semibold;
    text-decoration: underline;
    cursor: pointer;
    color: $grey;
    position: absolute;
    top: 2px;
    right: 0;

    &:hover {
      text-decoration: none;
    }
  }

  &__form-button {
    margin-top: $indent-large !important;
  }
}
</style>
